import React, { useEffect } from "react";

import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  FormControlLabel,
  RadioGroup,
  Radio,
  CircularProgress,
} from "@mui/material";

import CollapseCard from "components/Collapse";
import TwoColumnContainer from "components/Container/TwoColumnContainer";
import InputField from "components/InputField";
import IpInputField from "components/InputField/IpInputField";
import { isValidIpV4, isValidSubnetMask, isSameSubnet } from "utils/network-validation";

import { ReactComponent as CheckedIcon } from "assets/images/icons/icn_checked.svg";
import { ReactComponent as UnCheckedIconIcon } from "assets/images/icons/icn_unchecked.svg";

// Const
import { formLimitation } from 'const';

const getDefaultIpv4Data = (ipv4 = {}) => ({
  gateway: ipv4?.gateway ?? null,
  ipAddress: ipv4?.ipAddress ?? null,
  primaryDns: ipv4?.primaryDns ?? null,
  secondaryDns: ipv4?.secondaryDns ?? null,
  subnetMask: ipv4?.subnetMask ?? null,
  type: ipv4?.type || "dhcp",
});

const getDefaultIpv6Data = (ipv6 = {}) => ({
  gateway: ipv6?.gateway ?? null,
  ipAddress: ipv6?.ipAddress ?? null,
  prefixLength: ipv6?.prefixLength ?? null,
  primaryDns: ipv6?.primaryDns ?? null,
  secondaryDns: ipv6?.secondaryDns ?? null,
  type: ipv6?.type || "disable",
});

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.customFontSize[2],
  color: theme.palette.primary.black,
  fontWeight: theme.typography.customFontWeight[1],
}));

const StyledHelperText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.customFontSize[0],
  color: theme.palette.primary.greySecound,
  marginTop: "4px",
  marginLeft: "4px",
}))

const RenderRadioGroupField = ({ viewerPermissions, value, options, onChange }) => {
  const theme = useTheme();
  return (
    <RadioGroup row value={value} onChange={onChange}>
      {options.map((option) => (
        <FormControlLabel
          key={option.value}
          value={option.value}
          label={option.label}
          disabled={viewerPermissions}
          control={
            <Radio icon={<UnCheckedIconIcon />} checkedIcon={<CheckedIcon />} />
          }
          sx={{
            "& .MuiFormControlLabel-label": {
              whiteSpace: "pre-line",
              fontSize: theme.typography.customFontSize[2],
              color: theme.palette.primary.greyPrimary,
            },
          }}
        />
      ))}
    </RadioGroup>
  );
};
const checkIpFieldInvalid = (key, val, allData) => {
  if (key === "ipAddress") {
    return !val || !isValidIpV4(val);
  }
  if (key === "subnetMask") {
    return !val || !isValidIpV4(val);
  }
  if (key === "gateway") {
    return !val || !isValidIpV4(val) || !isSameSubnet(allData.ipAddress, val, allData.subnetMask);
  }
  if (key === "primaryDns") {
    return !val || !isValidIpV4(val);
  }
  if (key === "secondaryDns") {
    return val ? !isValidIpV4(val) : false;
  }
  return false;
};

const RenderIpFields = ({ viewerPermissions, fields, data, setData, isIpv4 }) => {
  const theme = useTheme();
  const [touched, setTouched] = React.useState({});

  return fields.map(({ key, label, detail }) => {
    const isInvalid = (isIpv4 && touched[key]) ? checkIpFieldInvalid(key, data[key], data) : false;
    return (
      <TwoColumnContainer key={key} label={label}>
        <Box width="325px">
          {
            viewerPermissions
              ? <StyledTypography sx={{ marginTop: "16px" }}>
                {data[key] || "-"}
              </StyledTypography>
              : (
                <Box
                  sx={
                    isInvalid
                      ? {
                          "& > div": {
                            borderColor: `${theme.palette.error.main} !important`,
                          },
                        }
                      : {}
                  }
                >
                  <IpInputField
                    value={data[key] || ""}
                    onChange={(val) => {
                      setTouched((prev) => ({ ...prev, [key]: true }));
                      setData((prev) => ({ ...prev, [key]: val }));
                    }}
                  />
                </Box>
              )
          }
        </Box>
        {detail && (
          <StyledTypography
            sx={{
              padding: "4px 8px",
              fontSize: theme.typography.customFontSize[1],
              color: theme.palette.primary.greySecound,
            }}
          >
            {detail}
          </StyledTypography>
        )}
      </TwoColumnContainer>
    );
  });
};

const NetWorkPanel = ({
  viewerPermissions,
  networkPermissions,
  setDataChangesFlag,
  handleDataChanged,
  dataCancelFlag,
  setDataCancelFlag,
  loading,
  originalData,
  networkInfoData,
  setNetworkInfoData,
  networkDeviceData,
  setNetworkDeviceData,
  ipv4Data,
  setIpv4Data,
  ipv6Data,
  setIpv6Data,
  capabilityData,
  infoData
}) => {
  const isSurveillanceDevice = ["nvr", "ipcam"].includes(infoData?.deviceType);

  function handleRadioChange(setter, newType, resetFieldsMap) {
    const resetValues = resetFieldsMap[newType] || {};
    setter((prev) => ({
      ...prev,
      type: newType,
      ...resetValues,
    }));
  }

  function hasNetworkChanged() {
    if (!originalData.deviceNetwork) return false;

    const deviceNetworkOrig = originalData.deviceNetwork;

    if (!isSurveillanceDevice && networkDeviceData.mgmtVlan !== deviceNetworkOrig.mgmtVlan) return true;

    const originalIpv4 = getDefaultIpv4Data(deviceNetworkOrig.ipv4);
    const originalIpv6 = getDefaultIpv6Data(deviceNetworkOrig.ipv6);

    if (ipv4Data.type !== originalIpv4.type) return true;
    if (ipv4Data.type === "static") {
      for (let key of [
        "ipAddress",
        "subnetMask",
        "gateway",
        "primaryDns",
        "secondaryDns",
      ]) {
        if (ipv4Data[key] !== originalIpv4[key]) return true;
      }
    }

    if (isSurveillanceDevice || !networkPermissions.ipv6) return false;

    if (ipv6Data.type !== originalIpv6.type) return true;
    if (ipv6Data.type === "dynamic") {
      for (let key of ["primaryDns", "secondaryDns"]) {
        if (ipv6Data[key] !== originalIpv6[key]) return true;
      }
    }
    if (ipv6Data.type === "static") {
      for (let key of [
        "ipAddress",
        "prefixLength",
        "gateway",
        "primaryDns",
        "secondaryDns",
      ]) {
        if (ipv6Data[key] !== originalIpv6[key]) return true;
      }
    }

    return false;
  }

  useEffect(() => {
    const changed = hasNetworkChanged();
    setDataChangesFlag((prev) => ({ ...prev, netWork: changed }));
    if (changed) {
      handleDataChanged({
        data: {
          info: networkInfoData,
          deviceNetwork: {
            mgmtVlan: networkDeviceData.mgmtVlan,
            ipv4: ipv4Data,
            ipv6: ipv6Data,
          },
        },
      });
    }
  }, [ipv4Data, ipv6Data, networkDeviceData, originalData]);

  useEffect(() => {
    if (dataCancelFlag.netWork === true && originalData.deviceNetwork) {
      const deviceNetworkData = originalData.deviceNetwork;

      setNetworkInfoData(originalData.info);
      setNetworkDeviceData({ mgmtVlan: deviceNetworkData.mgmtVlan });
      setIpv4Data(getDefaultIpv4Data(deviceNetworkData.ipv4));
      setIpv6Data(getDefaultIpv6Data(deviceNetworkData.ipv6));

      setDataCancelFlag((prev) => ({ ...prev, netWork: false }));
    }
  }, [dataCancelFlag, originalData]);

  const ipv4Field = [
    { value: "dhcp", label: "DHCP" },
    { value: "static", label: "Static" },
  ];

  const ipv4Staticfields = [
    { key: "ipAddress", label: "IP Address" },
    { key: "subnetMask", label: "IP Mask" },
    { key: "gateway", label: "Gateway" },
    { key: "primaryDns", label: "Primary DNS Server" },
    { key: "secondaryDns", label: "Secondary DNS Server (Optional)" },
  ];

  const ipv6Field = [
    { value: "disable", label: "Disable" },
    { value: "dynamic", label: "Dynamic IP\n[SLAAC/DHCPv6]" },
    { value: "static", label: "Static" },
  ];

  const ipv6Dynamicfields = [
    { key: "primaryDns", label: "Primary DNS Server (Optional)" },
    { key: "secondaryDns", label: "Secondary DNS Server (Optional)" },
  ];

  const ipv6Staticfields = [
    { key: "ipAddress", label: "IPv6 Address", detail: "(Format 2001:)" },
    { key: "prefixLength", label: "Prefix Length", detail: "(1-128)" },
    { key: "gateway", label: "Default Gateway (Optional)" },
    { key: "primaryDns", label: "Primary DNS Server (Optional)" },
    { key: "secondaryDns", label: "Secondary DNS Server (Optional)" },
  ];

  return (
    capabilityData.tunnelAccess === true && !isSurveillanceDevice ? null : (<CollapseCard title="Network" minWidth="550px">
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {!isSurveillanceDevice && (
            <TwoColumnContainer label="Management VLAN">
              <Box width="325px">
                {
                  viewerPermissions
                    ? <StyledTypography>{networkDeviceData.mgmtVlan || "-"}</StyledTypography>
                    :
                    <>
                      <InputField
                        type="number"
                        value={networkDeviceData.mgmtVlan || ""}
                        isValid={+networkDeviceData?.mgmtVlan >= formLimitation.vlanId.min && +networkDeviceData?.mgmtVlan <= formLimitation.vlanId.max}
                        onChange={value => { setNetworkDeviceData(() => ({ mgmtVlan: +value })) }}
                      />
                      <StyledHelperText>
                        {`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                      </StyledHelperText>
                    </>
                }
              </Box>
            </TwoColumnContainer>
          )}
          {networkPermissions.ipv4 && (
            <TwoColumnContainer label="IPv4">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <RenderRadioGroupField
                  viewerPermissions={viewerPermissions}
                  value={ipv4Data.type}
                  options={ipv4Field}
                  onChange={(e) =>
                    handleRadioChange(setIpv4Data, e.target.value, {
                      dhcp: {
                        ipAddress: null,
                        subnetMask: null,
                        gateway: null,
                        primaryDns: null,
                        secondaryDns: null,
                      },
                      static: {
                        ipAddress: null,
                        subnetMask: null,
                        gateway: null,
                        primaryDns: null,
                        secondaryDns: null,
                      },
                    })
                  }
                />
              </Box>
            </TwoColumnContainer>
          )}
          {ipv4Data.type === "static" && (
            <RenderIpFields
              viewerPermissions={viewerPermissions}
              fields={ipv4Staticfields}
              data={ipv4Data}
              setData={setIpv4Data}
              isIpv4={true}
            />
          )}
          {networkPermissions.ipv6 && (
            <TwoColumnContainer label="IPv6">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  <RenderRadioGroupField
                    viewerPermissions={viewerPermissions}
                    value={ipv6Data.type}
                    options={ipv6Field}
                    onChange={(e) =>
                      handleRadioChange(setIpv6Data, e.target.value, {
                        disable: {
                          ipAddress: null,
                          prefixLength: null,
                          gateway: null,
                          primaryDns: null,
                          secondaryDns: null,
                        },
                        dynamic: { primaryDns: null, secondaryDns: null },
                        static: {
                          ipAddress: null,
                          prefixLength: null,
                          gateway: null,
                          primaryDns: null,
                          secondaryDns: null,
                        },
                      })
                    }
                  />
                </Box>
              </Box>
            </TwoColumnContainer>
          )}
          {ipv6Data.type === "dynamic" && (
            <RenderIpFields
              viewerPermissions={viewerPermissions}
              fields={ipv6Dynamicfields}
              data={ipv6Data}
              setData={setIpv6Data}
            />
          )}
          {ipv6Data.type === "static" && (
            <RenderIpFields
              viewerPermissions={viewerPermissions}
              fields={ipv6Staticfields}
              data={ipv6Data}
              setData={setIpv6Data}
            />
          )}
        </>
      )}
    </CollapseCard>)
  );
};

export default NetWorkPanel;
